/**
 * An ice rink: a session at the hire desk (skates included; a day's session lets you back on), the ice itself, the
 * stands, a hot chocolate at the café. You get better the more you skate: steadier on your feet, fewer wobbles.
 */
import { advance, spend, type LifeEvent, type LifeState } from './life.ts';
import { localPrice } from './character.ts';

const RINKS: Record<string, string> = {
  lagos: 'Arctic Bay Ice Rink, Lekki', us: 'Rockport Ice Rink, Manhattan', france: 'Patinoire du Canal, Paris',
  china: 'Silver Lake Ice Arena, Shanghai', japan: 'Hyōga Skate Hall, Tokyo', italy: 'Palaghiaccio Aurora, Rome', uae: 'Polar Dome Rink, Dubai',
};
export const rinkFor = (region: string): string => RINKS[region] ?? RINKS.lagos;
export const rinkSession = (s: LifeState) => localPrice(s, 7_500, 500);
export const cocoaPrice = (s: LifeState) => localPrice(s, 2_000, 100);

interface RinkState { day?: number; visits?: number; /** seconds on the ice, all told */ secs?: number; cocoaDay?: number }
const of = (s: LifeState): RinkState => ((s as LifeState & { rink?: RinkState }).rink ??= {});

export const hasRinkSession = (s: LifeState) => of(s).day === Math.floor(s.minutes / 1440);
/** how good you are on skates, 0..100 (an hour on the ice, all told, and you're steady) */
export const skateSkill = (s: LifeState) => Math.min(100, Math.round((of(s).secs ?? 0) / 36));

export function buyRinkSession(s: LifeState): { ok: boolean; line: string } {
  if (hasRinkSession(s)) return { ok: true, line: 'Your session from earlier still counts. Your skates are where you left them.' };
  if (!spend(s, rinkSession(s), 'Ice rink session + skate hire')) return { ok: false, line: "You can't afford a session." };
  const r = of(s);
  r.day = Math.floor(s.minutes / 1440); r.visits = (r.visits ?? 0) + 1;
  if (r.visits === 1) s.history.push({ t: s.minutes, text: 'A first time on ice skates.' });
  return { ok: true, line: r.visits === 1 ? '"What size?" A pair of hire skates, laced tight. Through the gate in the boards: the ice.' : 'Your size, laced tight. The gate in the boards is open.' };
}

/** off the ice after a while on it: fun for the time, a little skill, and a line for how it went */
export function skated(s: LifeState, secs: number): { line: string; events: LifeEvent[] } {
  const r = of(s), before = skateSkill(s);
  r.secs = (r.secs ?? 0) + secs;
  const mins = Math.max(1, Math.round(secs / 60 * 10)); // (game minutes: a minute of play is ten on the clock, like the rest)
  s.needs.fun = Math.min(100, s.needs.fun + Math.min(14, 3 + secs / 15));
  s.needs.energy = Math.max(0, s.needs.energy - Math.min(8, secs / 30));
  const after = skateSkill(s);
  const line = after >= 60 && before < 60 ? 'You glide like you were born on ice. People at the rail are watching.'
    : after >= 25 && before < 25 ? "You've found your feet: long pushes, no more flailing."
    : before < 10 ? 'Off the ice, legs like jelly, grinning.' : 'Off the ice, warm in the cold air.';
  return { line, events: advance(s, Math.min(60, mins), { busy: true }) };
}

export function buyCocoa(s: LifeState): { ok: boolean; line: string; events: LifeEvent[] } {
  if (!spend(s, cocoaPrice(s), 'Hot chocolate at the rink')) return { ok: false, line: "You can't afford it.", events: [] };
  const r = of(s), first = r.cocoaDay !== Math.floor(s.minutes / 1440);
  r.cocoaDay = Math.floor(s.minutes / 1440);
  s.needs.hunger = Math.min(100, s.needs.hunger + 6);
  s.needs.fun = Math.min(100, s.needs.fun + (first ? 4 : 1));
  return { ok: true, line: first ? 'A hot chocolate, whipped cream on top, both hands round the cup. Your fingers come back to life.' : 'Another hot chocolate. Worth it.', events: advance(s, 8, { busy: true }) };
}

export const lessonPrice = (s: LifeState) => localPrice(s, 6_000, 500);
/** ten minutes with the instructor: how to push, how to stop; worth a good while of finding it out alone */
export function rinkLesson(s: LifeState): { ok: boolean; line: string; events: LifeEvent[] } {
  if (!hasRinkSession(s)) return { ok: false, line: 'Get your skates at the hire desk first.', events: [] };
  if (!spend(s, lessonPrice(s), 'Skating lesson')) return { ok: false, line: "You can't afford a lesson.", events: [] };
  const r = of(s), before = skateSkill(s);
  r.secs = (r.secs ?? 0) + 600;
  return { ok: true, line: before < 15 ? '"Knees bent, push out to the side, not back. Arms out. Now glide." By the end you can stop without the boards.' : '"Cross over on the turns: right foot over the left." You get it on the third try.', events: advance(s, 10, { busy: true }) };
}
